import { useEffect, useRef, useState } from 'react';
import { type Control, get, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import type { ClarifyCatalog, ProviderFieldSchema, ProviderRow } from '@/domain/providerConfig';
import { toFormKey } from '@/routes/shared/ProviderConfig/form/formKeys';
import { ProviderSchemaField } from '@/routes/shared/ProviderConfig/ProviderSchemaField';
import { unreadableAt } from '@/routes/shared/ProviderConfig/schemaFields';

import type { FieldConflict } from '../form/conflictMerge';
import type { ProviderFormValues } from '../form/formModel';

import { ClarifyItemPicker } from './ClarifyItemPicker';
import { conflictAt, SIGNAL_PREFIX } from './sectionFields';
import { isScadaOnlyPen, splitSignals } from './signalVisibility';

import layout from './fieldLayout.module.scss';
import own from './PenSignalFields.module.scss';
import styles from './sections.module.scss';

export interface PenSignalFieldsProps {
    base: `pens.${string}`;
    row: ProviderRow | null;
    signalFields: ProviderFieldSchema[];
    control: Control<ProviderFormValues>;
    catalog: ClarifyCatalog | null;
    conflicts: FieldConflict[];
    /** OptoScale admins see each signal's stored key next to its label. */
    showKeys: boolean;
}

/**
 * The pen's mapping onto the provider's items, one per signal. It is the configuration a pen
 * cannot do without, so it is always shown, never behind a toggle.
 */
function PenSignalFields({
    base,
    row,
    signalFields,
    control,
    catalog,
    conflicts,
    showKeys,
}: PenSignalFieldsProps) {
    const { t } = useTranslation();
    const [added, setAdded] = useState<ReadonlySet<string>>(new Set());
    const [choice, setChoice] = useState<string | null>(null);
    // The box just added, to move focus into once it is on screen.
    const [justAdded, setJustAdded] = useState<string | null>(null);
    const fieldsRef = useRef<HTMLDivElement>(null);
    const formValues = useWatch({ control, name: `${base}.fields` });
    const { errors } = useFormState({ control, name: base });
    useEffect(() => {
        if (justAdded === null) return;
        const name = `${base}.fields.${toFormKey(justAdded)}`;
        fieldsRef.current?.querySelector<HTMLElement>(`[name="${name}"]`)?.focus();
        setJustAdded(null);
    }, [justAdded, base]);
    if (signalFields.length === 0) return null;
    const nameOf = (field: ProviderFieldSchema): `pens.${string}.fields.${string}` =>
        `${base}.fields.${toFormKey(field.key)}`;
    // Every signal has a picker, so each names its signal to be told apart.
    const subjectOf = (field: ProviderFieldSchema): string =>
        field.subject === null ? t(field.labelKey) : t(`providerConfig.subjects.${field.subject}`);
    const { shown, hidden } = splitSignals(signalFields, {
        formValues,
        stored: row?.fields ?? null,
        added,
        needsAttention: (field) =>
            get(errors, nameOf(field)) !== undefined ||
            conflictAt(conflicts, nameOf(field)) !== null ||
            unreadableAt(row, field.key).length > 0,
    });
    // The first hidden signal until one is chosen, so Add always has something to add.
    const chosen = hidden.some((field) => field.key === choice)
        ? (choice ?? '')
        : (hidden[0]?.key ?? '');
    // The save rules do not require a signal of a SCADA-only pen, so neither does its mark.
    const scadaOnly = isScadaOnlyPen(signalFields, { formValues });

    return (
        <fieldset className={styles.group}>
            <legend className={styles.subheading}>{t('providerConfig.pens.signals')}</legend>
            <div ref={fieldsRef} className={styles.fields}>
                <div className={layout.grid}>
                    {shown.map((field) => (
                        <ProviderSchemaField<ProviderFormValues>
                            key={field.key}
                            field={scadaOnly ? { ...field, required: false } : field}
                            control={control}
                            name={nameOf(field)}
                            conflict={conflictAt(conflicts, nameOf(field))}
                            dbKey={showKeys ? field.key.slice(SIGNAL_PREFIX.length) : undefined}
                            unreadable={unreadableAt(row, field.key)}
                            trailing={
                                catalog
                                    ? (binding) => (
                                          <ClarifyItemPicker
                                              items={catalog.items}
                                              value={binding.value}
                                              onChange={binding.onChange}
                                              label={t('providerConfig.pens.pickItem', {
                                                  subject: subjectOf(field),
                                              })}
                                          />
                                      )
                                    : undefined
                            }
                        />
                    ))}
                </div>
                {hidden.length > 0 && (
                    <FormField
                        className={own.addSignal}
                        label={t('providerConfig.pens.mapAnotherSignal', { count: hidden.length })}>
                        <div className={own.addSignalRow}>
                            {/* Choosing alone adds nothing: arrowing through a closed select
                                fires change on every press in some browsers. */}
                            <Select
                                value={chosen}
                                onChange={(event) => setChoice(event.target.value)}>
                                {hidden.map((field) => (
                                    <option key={field.key} value={field.key}>
                                        {subjectOf(field)}
                                    </option>
                                ))}
                            </Select>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setAdded(new Set([...added, chosen]));
                                    setChoice(null);
                                    setJustAdded(chosen);
                                }}>
                                {t('providerConfig.pens.addSignal')}
                            </Button>
                        </div>
                    </FormField>
                )}
            </div>
        </fieldset>
    );
}

export default PenSignalFields;
export { PenSignalFields };
